import { Component, Input, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import moment from 'moment/moment';

@Component({
  selector: 'ebo-custom-picker',
  templateUrl: './custom-picker.html',
  styleUrls: ['./custom-picker.scss']
})
export class CustomPickerComponent implements OnInit {
  @Input() title?: string;
  @Input() pickerValue: FormControl;
  @Input() pickerState?: FormControl;
  @Input() mode: 'date' | 'time' | 'datetime';
  @Input() minDate?: Date | null;
  initialValue: string;

  get displayTitle(): string {
    if (this.title && this.title.trim().length > 0) {
      return this.title;
    }

    if (this.mode === 'time') {
      return 'Time';
    }

    if (this.mode === 'datetime') {
      return 'Date & Time';
    }

    return 'Date';
  }

  ngOnInit(): void {
    this.initialValue = this.pickerValue.value;

    this.normalizeDateControlValue(this.pickerValue);
    this.pickerValue.valueChanges.subscribe(value => {
      if (value !== this.initialValue && this.pickerState.value !== 1) {
        this.pickerState.setValue(2);
      }
    });
  }

  /**
   * Handle manual date input on blur - captures the raw input value
   */
  onDateBlur(event: FocusEvent): void {
    const inputElement = event.target as HTMLInputElement;
    const inputValue = inputElement?.value;

    if (!inputValue) return;

    // Check if the current value is already a valid Date object
    const currentValue = this.pickerValue.value;
    if (currentValue instanceof Date && !isNaN(currentValue.getTime())) {
      // Check if the year seems wrong (e.g., 2002 instead of 2026)
      const inputParts = inputValue.split('/');
      if (inputParts.length === 3) {
        const inputYear = parseInt(inputParts[2], 10);
        const currentYear = currentValue.getFullYear();

        // If year in input has 4 digits and doesn't match, reparse
        if (inputYear >= 1000 && inputYear !== currentYear) {
          const reparsedDate = moment(inputValue, ['DD/MM/YYYY', 'D/M/YYYY'], true);
          if (reparsedDate.isValid() && !this.isBeforeMinDate(reparsedDate.toDate())) {
            this.pickerValue.setValue(reparsedDate.toDate(), { emitEvent: true });
          }
        }
      }
      return;
    }

    // Try to parse the manually entered date in various formats
    const formats = ['DD/MM/YYYY', 'D/M/YYYY', 'DD-MM-YYYY', 'D-M-YYYY', 'YYYY-MM-DD'];
    let parsedDate: moment.Moment | null = null;

    for (const format of formats) {
      const parsed = moment(inputValue, format, true);
      if (parsed.isValid()) {
        parsedDate = parsed;
        break;
      }
    }

    if (parsedDate && parsedDate.isValid() && !this.isBeforeMinDate(parsedDate.toDate())) {
      this.pickerValue.setValue(parsedDate.toDate(), { emitEvent: true });
    }
  }

  /**
   * Handle manual datetime input from the user (using native input event)
   */
  onManualDateTimeInput(event: Event): void {
    const inputValue = (event.target as HTMLInputElement)?.value;

    if (!inputValue) return;

    // Try to parse the manually entered datetime in various formats
    const formats = [
      'DD/MM/YYYY HH:mm',
      'DD/MM/YYYY H:mm',
      'D/M/YYYY HH:mm',
      'DD-MM-YYYY HH:mm',
      'YYYY-MM-DD HH:mm',
      'DD/MM/YYYY',
      'YYYY-MM-DDTHH:mm'
    ];
    let parsedDate: moment.Moment | null = null;

    for (const format of formats) {
      const parsed = moment(inputValue, format, true);
      if (parsed.isValid()) {
        parsedDate = parsed;
        break;
      }
    }

    if (parsedDate && parsedDate.isValid()) {
      this.pickerValue.setValue(parsedDate.toDate(), { emitEvent: true });
    }
  }

  normalizeDateControlValue(ctrl: FormControl): void {
    const raw = ctrl.value;

    if (!raw || typeof raw !== 'string') return;

    const parsed = moment(raw, 'DD/MM/YYYY', true);
    const isDateOnly = parsed.isValid() && !raw.includes(':') && !raw.includes('T');

    if (isDateOnly) {
      const jsDate = parsed.toDate();
      ctrl.setValue(jsDate, { emitEvent: false });
    }
  }

  private isBeforeMinDate(value: Date): boolean {
    if (!this.minDate || !(value instanceof Date) || Number.isNaN(value.getTime())) {
      return false;
    }

    const minDateStart = new Date(this.minDate);
    minDateStart.setHours(0, 0, 0, 0);

    const valueStart = new Date(value);
    valueStart.setHours(0, 0, 0, 0);

    return valueStart < minDateStart;
  }
}
